import React, { Fragment, useEffect, useRef, useState } from 'react';
import {
  Dialog,
  Transition,
  Combobox,
  TransitionChild,
  DialogPanel,
  DialogTitle,
  ComboboxInput, ComboboxButton, ComboboxOptions, ComboboxOption
} from '@headlessui/react';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import type { DeliveryFormData, Address } from '../types';
import { useDrivers } from '../hooks/useDrivers';
import { useDelivery } from '../hooks/useDelivery';
import { CheckIcon, ChevronUpDownIcon, MagnifyingGlassIcon } from "@heroicons/react/20/solid";

type SelectedDeliveryOrder = {
  store_id?: string | number | null;
  store?: {
    id?: string | number | null;
  } | null;
};

interface CreateDeliveryModalProps {
  isOpen: boolean;
  onClose: () => void;
  newDelivery: DeliveryFormData;
  onDeliveryChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onDriverChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onSubmit: (e: React.FormEvent) => void;
  loading: boolean;
  customerAddresses: Address[] | null;
  selectedOrder: SelectedDeliveryOrder | null;
}

const CreateDeliveryModal = ({
                               isOpen,
                               onClose,
                               newDelivery,
                               onDeliveryChange,
                               onDriverChange,
                               onSubmit,
                               loading,
                               customerAddresses,
                               selectedOrder,
                             }: CreateDeliveryModalProps) => {
  const { availableDrivers, fetchAvailableDrivers } = useDrivers();
  const {
    driverDistances,
    maxMultiDropKm,
    fetchDriverDistancesForDestination,
    clearDriverDistances,
  } = useDelivery();

  const [query, setQuery] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const hasFetchedDriversForOpenRef = useRef(false);

  // Fetch available drivers when modal opens
  useEffect(() => {
    if (!isOpen) {
      hasFetchedDriversForOpenRef.current = false;
      clearDriverDistances();
      setSubmitError(null);
      return;
    }

    if (hasFetchedDriversForOpenRef.current) return;

    hasFetchedDriversForOpenRef.current = true;
    void fetchAvailableDrivers();
  }, [isOpen, fetchAvailableDrivers, clearDriverDistances]);

  // Pre-fill destination
  useEffect(() => {
    if (!isOpen) return;
    if (customerAddresses?.[0] && !newDelivery.destination_id) {
      onDeliveryChange({
        target: { name: 'destination_id', value: customerAddresses[0].id }
      } as React.ChangeEvent<HTMLSelectElement>);
    }
  }, [isOpen, customerAddresses, newDelivery.destination_id, onDeliveryChange]);

  // New delivery destination address
  const newDestAddress =
      customerAddresses?.find(a => a.id === newDelivery.destination_id) ||
      customerAddresses?.[0];

  // Once we have drivers + destination, fetch active delivery distance info via hook.
  useEffect(() => {
    if (!newDestAddress?.latitude || !newDestAddress?.longitude) return;

    const orderStoreId = selectedOrder?.store_id || selectedOrder?.store?.id;
    if (!orderStoreId) return;

    const storeDrivers = availableDrivers.filter(d =>
        String(d.store_id) === String(orderStoreId)
    );

    if (storeDrivers.length === 0) {
      clearDriverDistances();
      return;
    }

    void fetchDriverDistancesForDestination(storeDrivers, newDestAddress);
  }, [
    availableDrivers,
    clearDriverDistances,
    fetchDriverDistancesForDestination,
    newDestAddress,
    selectedOrder,
  ]);

  const orderStoreId = selectedOrder?.store_id || selectedOrder?.store?.id;

  const filteredDrivers = availableDrivers?.filter((driver) => {
    if (!orderStoreId) return false;
    const matchesStore = String(driver.store_id) === String(orderStoreId);
    const matchesQuery = driver.driver_name.toLowerCase().includes(query.toLowerCase());
    if (!matchesStore || !matchesQuery) return false;

    const info = driverDistances[driver.auth_id];
    return !info?.tooFar;
  }) ?? [];

  const hasAlsoAssignedDrivers = filteredDrivers.some(
      d => driverDistances[d.auth_id]?.activeDeliveryStatus === 'DRIVER_ASSIGNED'
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    onSubmit(e);
  };

  const labelClass = 'block text-sm font-medium text-gray-800';

  return (
      <Transition show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={onClose}>
          <TransitionChild
              as={Fragment}
              enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
              leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-stone-900/60 backdrop-blur-[1px]" />
          </TransitionChild>

          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
              <TransitionChild
                  as={Fragment}
                  enter="ease-out duration-200" enterFrom="opacity-0 translate-y-2 sm:scale-95" enterTo="opacity-100 translate-y-0 sm:scale-100"
                  leave="ease-in duration-150" leaveFrom="opacity-100 translate-y-0 sm:scale-100" leaveTo="opacity-0 translate-y-2 sm:scale-95"
              >
                <DialogPanel className="relative w-full transform rounded-2xl bg-white ring-1 ring-stone-200 shadow-xl transition-all sm:max-w-xl">
                  <div className="absolute right-0 top-0 pr-4 pt-4">
                    <button
                        type="button"
                        className="rounded-md text-stone-400 hover:text-stone-600 focus:outline-none"
                        onClick={onClose}
                        aria-label="Close"
                    >
                      <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>

                  <div className="px-5 py-6 sm:px-6">
                    <DialogTitle as="h3" className="font-display text-xl tracking-tight text-gray-900">
                      Create delivery request
                    </DialogTitle>
                    <p className="mt-1 text-sm text-gray-600">Choose a destination and assign a driver.</p>

                    <form onSubmit={handleSubmit} className="mt-6 space-y-6">

                      {/* Submit error from DB trigger */}
                      {submitError && (
                          <div className="flex gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
                            <ExclamationTriangleIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-red-700">{submitError}</p>
                          </div>
                      )}

                      {/* Destination */}
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <label className={labelClass}>Delivery Destination</label>
                          <span className="text-[10px] font-bold text-stone-400 uppercase tracking-widest">
                              Prefilled from Customer Profile
                            </span>
                        </div>
                        {customerAddresses && customerAddresses.length > 0 ? (
                            <div className="text-sm bg-stone-50 p-3 rounded border border-stone-200">
                              {(() => {
                                const addr =
                                    customerAddresses.find(a => a.id === newDelivery.destination_id) ||
                                    customerAddresses[0];
                                return (
                                    <>
                                      <p className="font-medium text-stone-900">{addr.address_1}</p>
                                      {addr.address_2 && <p className="text-xs text-stone-600">{addr.address_2}</p>}
                                      <p className="text-xs text-stone-600 mt-1">
                                        {[addr.suburb, addr.city, addr.postcode].filter(Boolean).join(', ')}
                                      </p>
                                    </>
                                );
                              })()}
                            </div>
                        ) : (
                            <div className="p-4 bg-stone-50 text-stone-500 text-xs rounded-xl border border-stone-200 italic animate-pulse">
                              {loading ? 'Loading addresses...' : 'No delivery address found for this customer.'}
                            </div>
                        )}
                      </div>

                      {/* Driver */}
                      <div className="space-y-1">
                        <label className={labelClass}>Assign Driver</label>

                        {filteredDrivers.length === 0 && query === '' ? (
                            <p className="text-sm text-red-600 bg-red-50 p-3 rounded-lg border border-red-100">
                              {!orderStoreId
                                  ? "This order has no store assigned, so available drivers cannot be matched."
                                  : "There are no available drivers for this order's store."}
                            </p>
                        ) : (
                            <>
                              <Combobox
                                  value={newDelivery.driver_id}
                                  onChange={(val) => {
                                    setSubmitError(null);
                                    onDriverChange({
                                      target: { name: 'driver_id', value: val }
                                    } as React.ChangeEvent<HTMLSelectElement>);
                                  }}
                              >
                                <div className="relative mt-1">
                                  <div className="relative w-full cursor-default overflow-hidden rounded-lg bg-white text-left ring-1 ring-inset ring-stone-300 focus-within:ring-2 focus-within:ring-gray-900 transition-all">
                                    <ComboboxInput
                                        className="w-full border-none py-2.5 pl-10 pr-10 text-sm leading-5 text-gray-900 focus:ring-0 outline-none"
                                        displayValue={(driverId: string) =>
                                            availableDrivers?.find(d => d.auth_id === driverId)?.driver_name || ''
                                        }
                                        placeholder="Search drivers..."
                                        onChange={(e) => setQuery(e.target.value)}
                                    />
                                    <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                                      <MagnifyingGlassIcon className="h-4 w-4 text-stone-400" />
                                    </div>
                                    <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2">
                                      <ChevronUpDownIcon className="h-5 w-5 text-stone-400" aria-hidden="true" />
                                    </ComboboxButton>
                                  </div>

                                  <Transition
                                      as={Fragment}
                                      leave="transition ease-in duration-100"
                                      leaveFrom="opacity-100" leaveTo="opacity-0"
                                      afterLeave={() => setQuery('')}
                                  >
                                    <ComboboxOptions className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                      {filteredDrivers.length === 0 && query !== '' ? (
                                          <div className="cursor-default select-none py-2 px-4 text-gray-700 italic">
                                            No drivers found for "{query}"
                                          </div>
                                      ) : (
                                          filteredDrivers.map((driver) => {
                                            const info = driverDistances[driver.auth_id];
                                            const isAlsoAssigned = info?.activeDeliveryStatus === 'DRIVER_ASSIGNED';
                                            return (
                                                <ComboboxOption
                                                    key={driver.auth_id}
                                                    className={({ focus }) =>
                                                        `relative cursor-default select-none py-2.5 pl-10 pr-4 ${
                                                            focus ? 'bg-stone-50 text-stone-700' : 'text-gray-900'
                                                        }`
                                                    }
                                                    value={driver.auth_id}
                                                >
                                                  {({ selected, focus }) => (
                                                      <>
                                                        <div className="flex items-center justify-between gap-2 pr-1">
                                                          <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                                            {driver.driver_name}
                                                          </span>
                                                          {isAlsoAssigned && (
                                                              <span className="flex-shrink-0 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 whitespace-nowrap">
                                                                Also assigned
                                                                {info?.distanceKm != null
                                                                    ? ` · ${info.distanceKm.toFixed(1)}km away`
                                                                    : ''}
                                                              </span>
                                                          )}
                                                        </div>
                                                        {selected && (
                                                            <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${focus ? 'text-stone-700' : 'text-gray-900'}`}>
                                                              <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                                            </span>
                                                        )}
                                                      </>
                                                  )}
                                                </ComboboxOption>
                                            );
                                          })
                                      )}
                                    </ComboboxOptions>
                                  </Transition>
                                </div>
                              </Combobox>

                              {/* Multi-drop context note */}
                              {hasAlsoAssignedDrivers && (
                                  <p className="text-xs text-amber-600 mt-1.5 flex items-center gap-1.5">
                                    <ExclamationTriangleIcon className="h-3.5 w-3.5 flex-shrink-0" />
                                    Drivers marked "Also assigned" have an active delivery within {maxMultiDropKm}km of this destination.
                                  </p>
                              )}
                            </>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="pt-2 flex items-center justify-end gap-3">
                        <button
                            type="button"
                            className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-stone-300 hover:bg-stone-50"
                            onClick={onClose}
                        >
                          Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading || !newDelivery.driver_id}
                            aria-busy={loading}
                            className="inline-flex items-center rounded-md bg-gray-900 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-800 disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                          {loading ? 'Creating…' : 'Create delivery request'}
                        </button>
                      </div>
                    </form>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
  );
};

export default CreateDeliveryModal;